import { useCallback, useMemo, useReducer, useRef } from "react";

interface InputState {
  value: string;
  isTouched: boolean;
  options?: any;
}

interface InputActionType {
  type: string;
  value?: any;
}

const initialInputState: InputState = {
  value: "",
  isTouched: false,
};

const inputStateReducer = (state: InputState, action: InputActionType) => {
  switch (action.type) {
    case "INPUT":
      return {
        ...state,
        value:
          action.value === "true"
            ? true
            : action.value === "false"
            ? false
            : action.value,
      };
    case "BLUR":
      return { ...state, isTouched: true };
    case "RESET":
      return initialInputState;
    default:
      return state;
  }
};

export const useInput = (initialState?: InputState) => {
  let state = initialState;
  if (!state) {
    state = initialInputState;
  }

  const [inputState, dispatch] = useReducer(inputStateReducer, state);

  const ref = useRef(null);

  const valueChangeHandler = useCallback((event: Event) => {
    dispatch({
      type: "INPUT",
      value: (event.target as HTMLInputElement).value,
    });
  }, []);

  const valueCheckedHandler = useCallback((event: Event) => {
    dispatch({
      type: "INPUT",
      value: !(event.target as HTMLInputElement).checked,
    });
  }, []);

  const setValue = useCallback((value: any) => {
    dispatch({
      type: "INPUT",
      value: value,
    });
  }, []);

  const inputBlurHandler = useCallback((event: any) => {
    dispatch({ type: "BLUR" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const decimalValueHandler = useCallback((event: Event) => {
    dispatch({
      type: "INPUT",
      value: formatDecimal((event.target as HTMLInputElement).value),
    });
  }, []);

  const formatDecimal = (input: string): string => {
    const numericValue = input.replace(/[^0-9]/g, "");
    if (numericValue === "") return "";
    const intValue = parseInt(numericValue, 10);
    if (!isNaN(intValue)) {
      return (intValue / 100).toFixed(2);
    } else {
      return "";
    }
  };

  const generateReturnObj = useMemo(() => {
    return {
      value: inputState.value,
      isTouched: inputState.isTouched,
      ref: ref,
      valueChangeHandler,
      decimalValueHandler,
      inputBlurHandler,
      valueCheckedHandler,
      reset,
      setValue,
    };
  }, [
    inputState,
    valueChangeHandler,
    decimalValueHandler,
    inputBlurHandler,
    reset,
    valueCheckedHandler,
    setValue,
  ]);

  return generateReturnObj;
};

export type UseInput = {
  value: any;
  isTouched: boolean;
  ref: React.MutableRefObject<any>;
  valueChangeHandler: (event: Event) => void;
  decimalValueHandler: (event: Event) => void;
  valueCheckedHandler: (event: Event) => void;
  inputBlurHandler: (event: any) => void;
  reset: () => void;
  setValue: (val: any) => void;
};
